import i18n from "i18next";
import i18nChainedBackend from "i18next-chained-backend";
import i18nLocalStorageBackend from "i18next-localstorage-backend";
import i18nXhrBackend from "i18next-xhr-backend";
import intervalPlural from "i18next-intervalplural-postprocessor";

import moment from "moment-timezone";
import type { IProfile } from "App/Modules/Models";

import { initReactI18next } from "react-i18next";
import { Config } from "App/Modules/Utils";
import { setLocale as yupSetLocale } from "yup";
import { getIrStore } from "./storage";

export const getUserLanguage = () => {
    let language: string =
        getIrStore().get("profile")?.language?.key || getIrStore().get("locale") || getBrowserLanguage();

    if (language && language.length > 2) {
        language = language.substr(0, 2);
    }
    return language;
};

export const initLanguageHandling = async () => {
    await i18n
        .use(i18nChainedBackend)
        .use(initReactI18next)
        .use(intervalPlural)
        .init({
            backend: {
                backends: [
                    i18nLocalStorageBackend, // primary
                    i18nXhrBackend, // fallback
                ],
                backendOptions: [
                    {
                        // Cache backend options
                        // prefix for stored languages
                        prefix: "ir.langs.",

                        // expiration (version (below) used instead)
                        // expirationTime: 7*24*60*60*1000,

                        // Version applied to all languages. Could be automated based on releases
                        // Current behaviour: Invalidate cache at midnight browser time
                        defaultVersion: new Date().toISOString().substring(0, 10),
                    },
                    {
                        // Xhr backend load
                        loadPath: `${Config.apiUrl}/translations?lang={{lng}}`,
                    },
                ],
            },
            lng: getUserLanguage(),
            fallbackLng: [],
            keySeparator: false,
            interpolation: {
                escapeValue: false,
                format: (value, format) => {
                    if (moment.isMoment(value)) return value.format(format || "L LT");
                    if (value instanceof Date) return moment(value).format(format || "L LT");
                    return value;
                },
            },
            debug: false,
        });

    const tValidation = (key: string) => (params: any) => {
        const t = i18n.t(`Validation.${key}`, params);
        return t;
    };

    yupSetLocale({
        mixed: {
            default: tValidation("Mixed.Default"),
            required: tValidation("Mixed.Required"),
        },
        string: {
            email: tValidation("String.Email"),
            length: tValidation("String.Length"),
            matches: tValidation("String.Matches"),
            max: tValidation("String.Max"),
            min: tValidation("String.Min"),
            url: tValidation("String.Url"),
        },
        number: {
            integer: tValidation("Number.Integer"),
            lessThan: tValidation("Number.LessThan"),
            max: tValidation("Number.Max"),
            min: tValidation("Number.Min"),
            moreThan: tValidation("Number.MoreTHan"),
            positive: tValidation("Number.Positive"),
            negative: tValidation("Number.Negative"),
        },
        date: {
            max: tValidation("Date.Max"),
            min: tValidation("Date.Min"),
        },
        array: {
            max: tValidation("Array.Max"),
            min: tValidation("Array.Min"),
        },
    });
};

const getUserProfileInternal = () => getIrStore().get("profile") as IProfile;

/** Will throw if profile is not available, caught by ErrorBoundary */
export const getUserProfile = () => {
    const profile = getIrStore().get("profile") as IProfile;
    if (!profile) {
        throw new Error("Profile not available. Please refresh page or login again.");
    }
    return profile;
};

export const isUserProfileAvailable = () => {
    const profile = getIrStore().get("profile") as IProfile;
    return !!profile;
};

export const getUserLocale = () =>
    (getUserProfileInternal()?.locale || getIrStore().get("locale") || getBrowserLanguage()) as string;

function getBrowserLanguage(): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nav = window.navigator as any;
    const browserLanguagePropertyKeys = ["language", "browserLanguage", "systemLanguage", "userLanguage"];
    let i: number;
    let language: string;

    // support for HTML 5.1 "navigator.languages"
    if (Array.isArray(nav.languages)) {
        for (i = 0; i < nav.languages.length; i++) {
            language = nav.languages[i];
            if (language && language.length) {
                return language;
            }
        }
    }

    // support for other well known properties in browsers
    for (i = 0; i < browserLanguagePropertyKeys.length; i++) {
        language = nav[browserLanguagePropertyKeys[i]];
        if (language && language.length) {
            return language;
        }
    }
    return "en";
}
